import React, { useState, useEffect } from 'react';
import { Interest } from './types';
import { profileService } from '../../services/Profile/profileService';
import {
  InterestSelectorContainer,
  InterestSelectorHeader,
  InterestSelectorTitle,
  InterestSelectorDescription,
  InterestCategories,
  CategoryTab,
  InterestGrid,
  InterestItem,
  InterestIcon,
  InterestName,
  SelectedInterests,
  SelectedInterest,
  InterestSearch,
  SearchInput,
  InterestCounter,
  ValidationMessage,
  InterestActions,
  ActionButton,
  ClearButton
} from '../../styles/Profile/ProfileEditor.styles';

interface InterestSelectorProps {
  interests: Interest[];
  onChange: (interests: Interest[]) => void;
}

const INTEREST_CATEGORIES = [
  { id: 'hobbies', name: 'Hobbies', icon: '🎨' },
  { id: 'sports', name: 'Sports', icon: '⚽' },
  { id: 'music', name: 'Music', icon: '🎵' },
  { id: 'movies', name: 'Movies', icon: '🎬' },
  { id: 'books', name: 'Books', icon: '📚' },
  { id: 'travel', name: 'Travel', icon: '✈️' },
  { id: 'food', name: 'Food', icon: '🍕' },
  { id: 'other', name: 'Other', icon: '🌟' }
];

const MIN_INTERESTS = 3;
const MAX_INTERESTS = 20;

export const InterestSelector: React.FC<InterestSelectorProps> = ({
  interests,
  onChange
}) => {
  const [selectedInterests, setSelectedInterests] = useState<Interest[]>(interests);
  const [activeCategory, setActiveCategory] = useState('hobbies');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredInterests, setFilteredInterests] = useState<Interest[]>([]);
  const [catalogByCategory, setCatalogByCategory] = useState<Record<string, Interest[]>>({});

  useEffect(() => {
    profileService.getInterestsCatalog()
      .then(catalog => {
        const grouped = catalog.reduce<Record<string, Interest[]>>((acc, interest) => {
          const category = interest.category || 'other';
          (acc[category] = acc[category] || []).push(interest);
          return acc;
        }, {});
        setCatalogByCategory(grouped);
      })
      .catch(error => console.error('Failed to load interests catalog:', error));
  }, []);


  const validateInterests = (): boolean => {
    return selectedInterests.length >= MIN_INTERESTS && selectedInterests.length <= MAX_INTERESTS;
  };

  const handleInterestToggle = (interest: Interest) => {
    const isSelected = selectedInterests.some(selected => selected.id === interest.id);

    if (isSelected) {
      setSelectedInterests(prev => prev.filter(selected => selected.id !== interest.id));
    } else {
      if (selectedInterests.length < MAX_INTERESTS) {
        setSelectedInterests(prev => [...prev, interest]);
      }
    }
  };

  const handleRemoveInterest = (interestId: string) => {
    setSelectedInterests(prev => prev.filter(interest => interest.id !== interestId));
  };



  const handleClearAll = () => {
    const confirmed = window.confirm('Are you sure you want to clear all selected interests?');
    if (confirmed) {
      setSelectedInterests([]);
    }
  };

  const filterInterests = (interests: Interest[], searchTerm: string): Interest[] => {
    if (!searchTerm) return interests;
    return interests.filter(interest =>
      interest.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  useEffect(() => {
    const categoryInterests = catalogByCategory[activeCategory] || [];
    const filtered = filterInterests(categoryInterests, searchTerm);
    setFilteredInterests(filtered);
  }, [activeCategory, searchTerm, catalogByCategory]);

  useEffect(() => {
    onChange(selectedInterests);
  }, [selectedInterests]);



  return (
    <InterestSelectorContainer>
      <InterestSelectorHeader>
        <InterestSelectorTitle>Manage Your Interests</InterestSelectorTitle>
        <InterestSelectorDescription>
          Choose interests that represent you. This helps others find common ground and start meaningful conversations.
        </InterestSelectorDescription>
      </InterestSelectorHeader>



      <InterestActions>
        <ClearButton onClick={handleClearAll}>
          Clear All
        </ClearButton>
      </InterestActions>

      <InterestSearch>
        <SearchInput
          type="text"
          placeholder="Search interests..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </InterestSearch>

      <div style={{ marginBottom: '0.5rem', fontWeight: 600, color: '#666', fontSize: '0.9rem' }}>
        Browse by Category:
      </div>
      <InterestCategories>
        {INTEREST_CATEGORIES.map(category => (
          <CategoryTab
            key={category.id}
            $isActive={activeCategory === category.id}
            onClick={() => setActiveCategory(category.id)}
          >
            <InterestIcon>{category.icon}</InterestIcon>
            <InterestName>{category.name}</InterestName>
          </CategoryTab>
        ))}
      </InterestCategories>

      <InterestGrid>
        {filteredInterests.map(interest => {
          const isSelected = selectedInterests.some(selected => selected.id === interest.id);
          return (
            <InterestItem
              key={interest.id}
              $isSelected={isSelected}
              onClick={() => handleInterestToggle(interest)}
            >
              <InterestIcon>{interest.icon}</InterestIcon>
              <InterestName>{interest.name}</InterestName>
            </InterestItem>
          );
        })}
      </InterestGrid>

      <SelectedInterests>
        <h4>Selected Interests ({selectedInterests.length}/{MAX_INTERESTS}):</h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1rem' }}>
          {selectedInterests.map(interest => (
            <SelectedInterest key={interest.id}>
              <InterestIcon>{interest.icon}</InterestIcon>
              <InterestName>{interest.name}</InterestName>
              <button
                onClick={() => handleRemoveInterest(interest.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer',
                  marginLeft: '0.5rem'
                }}
              >
                ×
              </button>
            </SelectedInterest>
          ))}
        </div>
      </SelectedInterests>

      <InterestCounter>
        {selectedInterests.length < MIN_INTERESTS ? (
          <ValidationMessage $type="warning">
            Please select at least {MIN_INTERESTS} interests to continue.
          </ValidationMessage>
        ) : selectedInterests.length > MAX_INTERESTS ? (
          <ValidationMessage $type="warning">
            You can select up to {MAX_INTERESTS} interests.
          </ValidationMessage>
        ) : (
          <div style={{ color: '#2ed573' }}>
            ✓ Great! You have selected {selectedInterests.length} interests.
          </div>
        )}
      </InterestCounter>
    </InterestSelectorContainer>
  );
};
